import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { api, apiUrl } from '@/api';
import { useAuthStore } from '@/hooks/useStore';
import type { User } from '@/types';

const NATIVE_OAUTH_CALLBACK_PATH = '/oauth/native/callback';
const NATIVE_OAUTH_CUSTOM_CALLBACK = 'com.fifty410.hrplatform.oauth://oauth/callback';
export const NATIVE_OAUTH_DEDUPE_WINDOW_MS = 1_500;
const NATIVE_OAUTH_DEDUPE_MAX_ENTRIES = 32;

const NATIVE_OAUTH_TRANSACTION_KEY = 'pm-native-oauth-transaction';
const WEB_OAUTH_TRANSACTION_KEY = 'pm-web-oauth-transaction';
const OAUTH_TRANSACTION_TTL_MS = 10 * 60 * 1_000;
const OAUTH_VALUE_PATTERN = /^[A-Za-z0-9_-]{32,128}$/;

export type OAuthReturnTarget = 'web' | 'native';
export type NativeOAuthCallbackMode = 'https' | 'custom-scheme';

export interface GoogleOAuthSession {
  dispose: () => Promise<void>;
}

export interface PendingOAuthTransaction {
  transactionId: string;
  verifier: string;
  createdAt: number;
  returnTarget: OAuthReturnTarget;
  authOwner: OAuthAuthOwner;
  returnPath?: string;
  nativeCallbackMode?: NativeOAuthCallbackMode;
}

export type PendingNativeOAuthTransaction = PendingOAuthTransaction;

interface ActiveNativeOAuthSession {
  listener?: PluginListenerHandle;
  cancel?: () => Promise<void>;
  cleanup?: Promise<void>;
  transactionId: string;
  settled: boolean;
  onBrowserFinished?: () => void;
  removeAbortListener?: () => void;
}

interface StartGoogleOAuthOptions {
  onBrowserFinished?: () => void;
  onBrowserFailed?: () => void;
  returnPath?: string;
  nativeCallbackMode?: NativeOAuthCallbackMode;
  signal?: AbortSignal;
}

export interface NativeOAuthCallbackBridge {
  addUrlOpenListener: (listener: (url: string) => void) => Promise<{ remove: () => Promise<void> }>;
  getLaunchUrl: () => Promise<string | null>;
  closeBrowser: () => Promise<void>;
  navigate: (route: string) => void;
}

interface NativeOAuthDeliveryTracker {
  handledSuccesses: Set<string>;
  recentlyHandledErrors: Map<string, number>;
}

let activeNativeOAuthSession: ActiveNativeOAuthSession | null = null;
let latestOAuthStart = 0;

interface OAuthAuthOwner {
  userId: string | null;
  isAuthenticated: boolean;
  hasToken: boolean;
}

function currentOAuthAuthOwner(): OAuthAuthOwner {
  const state = useAuthStore.getState();
  return {
    userId: state.user?.id ?? null,
    isAuthenticated: state.isAuthenticated,
    hasToken: Boolean(state.token),
  };
}

function sameOAuthAuthOwner(left: OAuthAuthOwner, right: OAuthAuthOwner): boolean {
  return (
    left.userId === right.userId &&
    left.isAuthenticated === right.isAuthenticated &&
    left.hasToken === right.hasToken
  );
}

/** Accept only an in-app path so authentication can never become an open redirect. */
export function sanitizeAuthReturnPath(candidate: string | null | undefined): string {
  if (!candidate || !candidate.startsWith('/') || candidate.startsWith('//')) return '/';
  try {
    const parsed = new URL(candidate, 'https://auth-return.invalid');
    if (parsed.origin !== 'https://auth-return.invalid') return '/';
    if (parsed.pathname === '/login' || parsed.pathname.startsWith('/oauth/')) return '/';
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '/';
  }
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomBase64Url(byteLength = 32): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return bytesToBase64Url(bytes);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return bytesToBase64Url(new Uint8Array(digest));
}

function transactionStorage(target: OAuthReturnTarget): Storage {
  return target === 'native' ? localStorage : sessionStorage;
}

function transactionStorageKey(target: OAuthReturnTarget): string {
  return target === 'native' ? NATIVE_OAUTH_TRANSACTION_KEY : WEB_OAUTH_TRANSACTION_KEY;
}

export function readOAuthTransaction(
  target: OAuthReturnTarget,
  now = Date.now(),
): PendingOAuthTransaction | null {
  try {
    const storage = transactionStorage(target);
    const key = transactionStorageKey(target);
    const stored = storage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<PendingOAuthTransaction>;
    const authOwner = parsed.authOwner;
    if (
      typeof parsed.transactionId !== 'string' ||
      typeof parsed.verifier !== 'string' ||
      typeof parsed.createdAt !== 'number' ||
      parsed.returnTarget !== target ||
      (parsed.nativeCallbackMode !== undefined &&
        parsed.nativeCallbackMode !== 'https' &&
        parsed.nativeCallbackMode !== 'custom-scheme') ||
      (target !== 'native' && parsed.nativeCallbackMode === 'custom-scheme') ||
      !authOwner ||
      !Object.prototype.hasOwnProperty.call(authOwner, 'userId') ||
      (authOwner.userId !== null && typeof authOwner.userId !== 'string') ||
      typeof authOwner.isAuthenticated !== 'boolean' ||
      typeof authOwner.hasToken !== 'boolean' ||
      !OAUTH_VALUE_PATTERN.test(parsed.transactionId) ||
      !OAUTH_VALUE_PATTERN.test(parsed.verifier) ||
      now - parsed.createdAt < 0 ||
      now - parsed.createdAt > OAUTH_TRANSACTION_TTL_MS
    ) {
      storage.removeItem(key);
      return null;
    }

    return parsed as PendingOAuthTransaction;
  } catch {
    return null;
  }
}

export function readNativeOAuthTransaction(now = Date.now()): PendingNativeOAuthTransaction | null {
  return readOAuthTransaction('native', now);
}

/** Recover a transaction's safe in-app destination before the transaction is cleared. */
export function getOAuthTransactionReturnPath(transactionId: string | null | undefined): string {
  if (!transactionId) return '/';

  const pending = (['native', 'web'] as const)
    .map((target) => readOAuthTransaction(target))
    .find((candidate) => candidate?.transactionId === transactionId);

  return sanitizeAuthReturnPath(pending?.returnPath);
}

export function clearOAuthTransaction(transactionId?: string): void {
  for (const target of ['native', 'web'] as const) {
    try {
      if (transactionId) {
        const pending = readOAuthTransaction(target);
        if (pending?.transactionId !== transactionId) continue;
      }
      transactionStorage(target).removeItem(transactionStorageKey(target));
    } catch {
      // Storage may be unavailable, but cleanup is best effort.
    }
  }
}

function clearNativeOAuthTransaction(transactionId?: string): void {
  try {
    if (transactionId) {
      const pending = readNativeOAuthTransaction();
      if (pending?.transactionId !== transactionId) return;
    }
    localStorage.removeItem(NATIVE_OAUTH_TRANSACTION_KEY);
  } catch {
    // Storage may be unavailable, but cleanup is best effort.
  }
}

export async function prepareGoogleOAuthRequest(
  target: OAuthReturnTarget,
  returnPath = '/',
  nativeCallbackMode: NativeOAuthCallbackMode = 'https',
  assertCurrent?: () => void,
): Promise<{
  url: URL;
  transaction: PendingOAuthTransaction;
  challenge: string;
}> {
  if (
    nativeCallbackMode === 'custom-scheme' &&
    (target !== 'native' || !Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios')
  ) {
    throw new Error('The alternate Google sign-in method is available only in the iOS app.');
  }
  const transaction: PendingOAuthTransaction = {
    transactionId: randomBase64Url(),
    verifier: randomBase64Url(),
    createdAt: Date.now(),
    returnTarget: target,
    authOwner: currentOAuthAuthOwner(),
    returnPath: sanitizeAuthReturnPath(returnPath),
    ...(target === 'native' ? { nativeCallbackMode } : {}),
  };
  const challenge = await pkceChallenge(transaction.verifier);
  const storage = transactionStorage(target);
  const key = transactionStorageKey(target);

  if (!sameOAuthAuthOwner(transaction.authOwner, currentOAuthAuthOwner())) {
    throw new Error('Authentication changed while Google sign-in was starting. Please try again.');
  }

  assertCurrent?.();
  storage.setItem(key, JSON.stringify(transaction));
  if (readOAuthTransaction(target)?.transactionId !== transaction.transactionId) {
    throw new Error('OAuth transaction could not be persisted');
  }

  const url = new URL(apiUrl('/auth/google'), window.location.origin);
  url.searchParams.set('return_to', target);
  url.searchParams.set('transaction', transaction.transactionId);
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  if (target === 'native' && nativeCallbackMode === 'custom-scheme') {
    url.searchParams.set('native_callback', 'custom-scheme');
  }
  return { url, transaction, challenge };
}

function settleNativeOAuthSession(
  session: ActiveNativeOAuthSession,
  notifyBrowserFinished: boolean,
  preserveTransaction = false,
): Promise<void> {
  if (session.settled) return session.cleanup ?? Promise.resolve();

  session.settled = true;
  session.removeAbortListener?.();
  session.removeAbortListener = undefined;
  if (activeNativeOAuthSession === session) activeNativeOAuthSession = null;
  if (!preserveTransaction) clearNativeOAuthTransaction(session.transactionId);

  const listener = session.listener;
  session.listener = undefined;
  session.cleanup = Promise.allSettled([
    ...(listener ? [listener.remove()] : []),
    ...(!preserveTransaction && session.cancel ? [session.cancel()] : []),
  ]).then(() => undefined);

  if (notifyBrowserFinished) session.onBrowserFinished?.();
  return session.cleanup;
}

/** Mark a native OAuth callback as accepted before closing the browser. */
function acceptNativeOAuthCallback(): void {
  if (activeNativeOAuthSession) {
    // OAuthCallback still needs the persisted verifier to exchange the handoff.
    void settleNativeOAuthSession(activeNativeOAuthSession, false, true);
  }
}

export async function startGoogleOAuth(
  options: StartGoogleOAuthOptions = {},
): Promise<GoogleOAuthSession> {
  if (options.signal?.aborted) throw new DOMException('Google sign-in was stopped.', 'AbortError');
  const start = ++latestOAuthStart;
  const startOwner = currentOAuthAuthOwner();
  const assertCurrent = () => {
    if (options.signal?.aborted || start !== latestOAuthStart) {
      throw new DOMException(
        'Google sign-in was stopped or replaced by a newer attempt.',
        'AbortError',
      );
    }
    if (!sameOAuthAuthOwner(startOwner, currentOAuthAuthOwner())) {
      throw new Error(
        'Authentication changed while Google sign-in was starting. Please try again.',
      );
    }
  };
  const returnTarget: OAuthReturnTarget = Capacitor.isNativePlatform() ? 'native' : 'web';
  const nativeCallbackMode = options.nativeCallbackMode ?? 'https';
  if (
    nativeCallbackMode === 'custom-scheme' &&
    (returnTarget !== 'native' || Capacitor.getPlatform() !== 'ios')
  ) {
    throw new Error('The alternate Google sign-in method is available only in the iOS app.');
  }

  if (nativeCallbackMode === 'custom-scheme') {
    const status = await api.get<{ configured?: unknown; nativeCallbackModes?: unknown }>(
      '/auth/google/status',
      undefined,
      { signal: options.signal },
    );
    assertCurrent();
    if (
      status.configured !== true ||
      !Array.isArray(status.nativeCallbackModes) ||
      !status.nativeCallbackModes.includes('custom-scheme')
    ) {
      throw new Error(
        'The alternate Google sign-in method is not available yet. Please use your workspace email or try again later.',
      );
    }
  }

  if (returnTarget === 'native') {
    if (activeNativeOAuthSession) {
      await settleNativeOAuthSession(activeNativeOAuthSession, false);
    }
    assertCurrent();

    const { transaction, url } = await prepareGoogleOAuthRequest(
      returnTarget,
      options.returnPath,
      nativeCallbackMode,
      assertCurrent,
    );
    try {
      assertCurrent();
    } catch (error) {
      clearNativeOAuthTransaction(transaction.transactionId);
      throw error;
    }

    const session: ActiveNativeOAuthSession = {
      transactionId: transaction.transactionId,
      settled: false,
      onBrowserFinished: options.onBrowserFinished,
    };
    activeNativeOAuthSession = session;
    const abortSession = () => {
      void settleNativeOAuthSession(session, false);
    };
    options.signal?.addEventListener('abort', abortSession, { once: true });
    session.removeAbortListener = () => options.signal?.removeEventListener('abort', abortSession);

    try {
      if (Capacitor.getPlatform() === 'ios') {
        const { NativeGoogleAuth } = await import('./nativeGoogleAuth');
        if (session.settled) return { dispose: () => settleNativeOAuthSession(session, false) };
        assertCurrent();
        const callbackUrl =
          nativeCallbackMode === 'custom-scheme'
            ? new URL(NATIVE_OAUTH_CUSTOM_CALLBACK)
            : new URL(NATIVE_OAUTH_CALLBACK_PATH, import.meta.env.VITE_PUBLIC_APP_URL);
        if (nativeCallbackMode === 'https' && callbackUrl.protocol !== 'https:') {
          throw new Error('OAuth callback must use HTTPS');
        }
        const listener = await NativeGoogleAuth.addListener(
          'authenticationFinished',
          ({ reason, transaction: finishedTransaction }) => {
            if (session.settled || finishedTransaction !== transaction.transactionId) return;
            void settleNativeOAuthSession(session, reason === 'cancelled');
            if (reason === 'failed') options.onBrowserFailed?.();
          },
        );
        if (session.settled) {
          await listener.remove();
          return { dispose: () => settleNativeOAuthSession(session, false) };
        }
        session.listener = listener;
        session.cancel = () => NativeGoogleAuth.cancel({ transaction: transaction.transactionId });
        assertCurrent();
        const { supported } = await NativeGoogleAuth.open({
          url: url.toString(),
          callbackUrl: callbackUrl.toString(),
        });
        if (supported || session.settled) {
          return { dispose: () => settleNativeOAuthSession(session, false) };
        }
        if (nativeCallbackMode === 'custom-scheme') {
          throw new Error('Update the iOS app to use the alternate Google sign-in method.');
        }
        // HTTPS authentication callbacks require iOS 17.4. Keep the existing
        // Universal Link flow available on older supported iOS releases.
        await listener.remove();
        session.listener = undefined;
        session.cancel = undefined;
      }

      const listener = await Browser.addListener('browserFinished', () => {
        void settleNativeOAuthSession(session, true);
      });

      if (session.settled) {
        await listener.remove();
      } else {
        session.listener = listener;
        assertCurrent();
        await Browser.open({ url: url.toString(), presentationStyle: 'fullscreen' });
      }
    } catch (error) {
      await settleNativeOAuthSession(session, false);
      throw error;
    }

    return {
      dispose: () => settleNativeOAuthSession(session, false),
    };
  }

  const { url, transaction } = await prepareGoogleOAuthRequest(
    returnTarget,
    options.returnPath,
    'https',
    assertCurrent,
  );
  try {
    assertCurrent();
  } catch (error) {
    clearOAuthTransaction(transaction.transactionId);
    throw error;
  }
  window.location.assign(url.toString());
  return { dispose: async () => undefined };
}

export async function exchangeOAuthHandoff(
  code: string,
  transactionId: string,
): Promise<{ token: string; user: User; returnPath: string }> {
  const pending = (['native', 'web'] as const)
    .map((target) => readOAuthTransaction(target))
    .find((candidate) => candidate?.transactionId === transactionId);
  if (
    !OAUTH_VALUE_PATTERN.test(code) ||
    !OAUTH_VALUE_PATTERN.test(transactionId) ||
    !pending ||
    pending.transactionId !== transactionId
  ) {
    throw new Error(
      'This Google sign-in callback was not initiated in this app. Please try again.',
    );
  }

  if (!sameOAuthAuthOwner(pending.authOwner, currentOAuthAuthOwner())) {
    clearOAuthTransaction(transactionId);
    throw new Error(
      'This Google sign-in was started under a different account. Your current account was left unchanged.',
    );
  }

  try {
    const response = await api.post<{ token: string; user: User }>('/auth/google/exchange', {
      code,
      transaction: transactionId,
      verifier: pending.verifier,
    });
    return {
      ...response,
      returnPath: sanitizeAuthReturnPath(pending.returnPath),
    };
  } finally {
    // A handoff is a single attempt. Network ambiguity is recovered by starting
    // a new OAuth flow rather than risking reuse of the verifier.
    clearOAuthTransaction(transactionId);
  }
}

export function nativeOAuthRoute(
  url: string,
  configuredOrigin: string | undefined = import.meta.env.VITE_PUBLIC_APP_URL,
): string | null {
  try {
    const parsed = new URL(url);
    const isCustomScheme = parsed.protocol === 'com.fifty410.hrplatform.oauth:';
    if (isCustomScheme) {
      if (
        !Capacitor.isNativePlatform() ||
        Capacitor.getPlatform() !== 'ios' ||
        parsed.host !== 'oauth' ||
        parsed.pathname !== '/callback' ||
        parsed.port ||
        parsed.username ||
        parsed.password
      )
        return null;
    } else {
      if (!configuredOrigin) return null;
      const expectedOrigin = new URL(configuredOrigin);
      if (expectedOrigin.protocol !== 'https:') return null;
      if (
        parsed.origin !== expectedOrigin.origin ||
        parsed.pathname !== NATIVE_OAUTH_CALLBACK_PATH ||
        parsed.username ||
        parsed.password
      )
        return null;
    }

    // Native success callbacks carry only a one-time handoff and transaction.
    // Reject legacy/replayed bearer fragments entirely.
    if (parsed.hash) return null;
    const error = parsed.searchParams.get('error');
    const code = parsed.searchParams.get('code');
    const transaction = parsed.searchParams.get('transaction');
    const pending = readNativeOAuthTransaction();
    if (isCustomScheme) {
      if (
        !transaction ||
        !OAUTH_VALUE_PATTERN.test(transaction) ||
        parsed.searchParams.getAll('transaction').length !== 1 ||
        parsed.searchParams.getAll('code').length > 1 ||
        parsed.searchParams.getAll('error').length > 1 ||
        Boolean(error && code) ||
        !pending ||
        pending.nativeCallbackMode !== 'custom-scheme' ||
        pending.transactionId !== transaction ||
        !sameOAuthAuthOwner(pending.authOwner, currentOAuthAuthOwner())
      )
        return null;
    } else if (
      pending?.transactionId === transaction &&
      pending.nativeCallbackMode === 'custom-scheme'
    ) {
      // Never allow a callback to switch the transport chosen for the transaction.
      return null;
    }
    if (!error && (!code || !transaction)) return null;
    if (code && (!OAUTH_VALUE_PATTERN.test(code) || !OAUTH_VALUE_PATTERN.test(transaction || ''))) {
      return null;
    }

    return `/oauth/callback${parsed.search}`;
  } catch {
    return null;
  }
}

function successfulHandoffIdentity(route: string): string | null {
  const parsed = new URL(route, 'https://oauth-callback.invalid');
  if (parsed.searchParams.has('error')) return null;

  const code = parsed.searchParams.get('code');
  const transaction = parsed.searchParams.get('transaction');
  return code && transaction ? `${code}:${transaction}` : null;
}

function rememberSuccessfulHandoff(handledSuccesses: Set<string>, identity: string): boolean {
  if (handledSuccesses.has(identity)) return true;

  handledSuccesses.add(identity);
  if (handledSuccesses.size > NATIVE_OAUTH_DEDUPE_MAX_ENTRIES) {
    const oldest = handledSuccesses.values().next().value as string | undefined;
    if (oldest) handledSuccesses.delete(oldest);
  }
  return false;
}

function wasRecentlyHandledError(recentlyHandledUrls: Map<string, number>, url: string): boolean {
  const now = Date.now();
  const previous = recentlyHandledUrls.get(url);
  if (previous !== undefined && now >= previous && now - previous < NATIVE_OAUTH_DEDUPE_WINDOW_MS) {
    return true;
  }

  recentlyHandledUrls.set(url, now);
  if (recentlyHandledUrls.size > NATIVE_OAUTH_DEDUPE_MAX_ENTRIES) {
    for (const [candidate, handledAt] of recentlyHandledUrls) {
      if (now < handledAt || now - handledAt >= NATIVE_OAUTH_DEDUPE_WINDOW_MS) {
        recentlyHandledUrls.delete(candidate);
      }
    }

    // A burst of distinct URLs can all be inside the dedupe window. Keep the
    // newest bounded set rather than allowing attacker-controlled links to grow
    // this listener's lifetime map without limit.
    while (recentlyHandledUrls.size > NATIVE_OAUTH_DEDUPE_MAX_ENTRIES) {
      const oldest = recentlyHandledUrls.keys().next().value as string | undefined;
      if (!oldest) break;
      recentlyHandledUrls.delete(oldest);
    }
  }
  return false;
}

/** Register both warm-app and cold-launch Universal Link callbacks. */
export async function registerNativeOAuthCallbacks(
  bridge: NativeOAuthCallbackBridge,
  configuredOrigin: string | undefined = import.meta.env.VITE_PUBLIC_APP_URL,
  deliveryTracker: NativeOAuthDeliveryTracker = {
    handledSuccesses: new Set<string>(),
    recentlyHandledErrors: new Map<string, number>(),
  },
): Promise<() => Promise<void>> {
  let active = true;
  let listener: { remove: () => Promise<void> } | undefined;

  const handleUrl = (url: string) => {
    if (!active) return;

    const route = nativeOAuthRoute(url, configuredOrigin);
    if (!route) return;

    // A previously opened link must not settle or replace the sign-in that is
    // currently presented. Its handoff belongs to a different device attempt.
    if (
      activeNativeOAuthSession &&
      new URL(route, 'https://oauth-callback.invalid').searchParams.get('transaction') !==
        activeNativeOAuthSession.transactionId
    )
      return;

    const successIdentity = successfulHandoffIdentity(route);
    const alreadyHandled = successIdentity
      ? rememberSuccessfulHandoff(deliveryTracker.handledSuccesses, successIdentity)
      : wasRecentlyHandledError(deliveryTracker.recentlyHandledErrors, url);
    if (alreadyHandled) return;

    acceptNativeOAuthCallback();
    void bridge.closeBrowser().catch(() => undefined);
    bridge.navigate(route);
  };

  try {
    listener = await bridge.addUrlOpenListener(handleUrl);
    const launchUrl = await bridge.getLaunchUrl();
    if (launchUrl) handleUrl(launchUrl);
  } catch (error) {
    active = false;
    await listener?.remove();
    throw error;
  }

  return async () => {
    active = false;
    await listener?.remove();
  };
}

/**
 * Keep native callback delivery installed across transient Capacitor setup
 * failures. The returned cleanup stops pending retries and removes a listener
 * that resolves after unmount.
 */
export function startRetryingNativeOAuthCallbacks(
  bridge: NativeOAuthCallbackBridge,
  options: {
    configuredOrigin?: string;
    retryDelayMs?: number;
    maxRetryDelayMs?: number;
    onRegistrationError?: (error: unknown) => void;
  } = {},
): () => Promise<void> {
  const retryDelayMs = Math.max(0, options.retryDelayMs ?? 1_000);
  const maxRetryDelayMs = Math.max(retryDelayMs, options.maxRetryDelayMs ?? 30_000);
  let active = true;
  let failureCount = 0;
  let listenerCleanup: (() => Promise<void>) | null = null;
  let pendingAttempt: Promise<void> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  const deliveryTracker: NativeOAuthDeliveryTracker = {
    handledSuccesses: new Set<string>(),
    recentlyHandledErrors: new Map<string, number>(),
  };

  const attemptRegistration = () => {
    if (!active || pendingAttempt || listenerCleanup) return;

    pendingAttempt = registerNativeOAuthCallbacks(bridge, options.configuredOrigin, deliveryTracker)
      .then(async (cleanup) => {
        failureCount = 0;
        if (!active) {
          await cleanup();
          return;
        }
        listenerCleanup = cleanup;
      })
      .catch((error: unknown) => {
        if (!active) return;

        failureCount += 1;
        const delay = Math.min(retryDelayMs * 2 ** (failureCount - 1), maxRetryDelayMs);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          attemptRegistration();
        }, delay);
        try {
          options.onRegistrationError?.(error);
        } catch {
          // Diagnostics must never disable the retry lifecycle.
        }
      })
      .finally(() => {
        pendingAttempt = null;
      });
  };

  attemptRegistration();

  return async () => {
    if (!active) return;
    active = false;
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }

    await pendingAttempt;
    const cleanup = listenerCleanup;
    listenerCleanup = null;
    if (cleanup) await cleanup();
  };
}
